import React, { useState, useContext, useEffect } from 'react';
import { Context as AuthContext } from 'contexts/auth';
import { Context as CompanyContext } from 'contexts/company';
import { Context as AlertContext } from 'contexts/alert';
import { useNavigate } from 'react-router-dom';
import { Config } from 'configs';
import { getFirstDayOfMonth, getLastDayOfMonth } from 'utils/dateManagement';
import { PageTitle } from 'components/layouts/pageTitle';
import { PageContent } from 'components/layouts/pageContent';
import { ListLayout } from 'components/layouts/listLayout';
import { NoRestaurantsBlock } from 'components/blocks/noRestaurants';
import { sortObjectArray } from 'utils/arrayHelpers';
import { updateOrderFields } from 'api/orders';
import { useFind } from 'hooks';
import { FormButton } from 'components/buttons/formButton';
import { OrderProgressPills } from './partials/orderProgressPills';
import { useOrders } from './hooks/useOrders';
import { useOrdersDisplay } from './hooks/useOrdersDisplay';
import { ClosedOrdersList } from './partials/closedOrders';
import { OrdersFilter } from './partials/ordersFilter';
import { ProcessingOrdersList } from './partials/processingOrders';
import { OrderSettingsEditForm } from './partials/_popup_settings';

export const Orders = () => {
  const navigate = useNavigate();

  const { state: auth } = useContext(AuthContext);
  const { state: companyState, getCompanyRestaurants, shouldDisplayNoRestaurantBlock, getOneByFilter, addNew, editById } = useContext(CompanyContext);
  const { alertController } = useContext(AlertContext);
  const [isExporting, setIsExporting] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [updatingSettings, setUpdatingSettings] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);

  const [isLoadingProgress, setIsLoadingProgress] = useState({});
  const [progressOrdersRefresher, setProgressOrdersRefresher] = useState(1);

  useEffect(() => {
    const findCompanySettings = async () => {
      const settings = await getOneByFilter({
        collectionName: Config.DATA.COLLECTIONS.COMPANY_DASHBOARD,
        token: auth.token,
        filter: { company: { $oid: auth.company?.company } },
        resourceV2: 'AdminCompanyDashboard',
        companyIdForV2: auth.company?.company,
      });

      if (settings) {
        setCompanySettings(settings);
      } else {
        await addNew({
          collectionName: Config.DATA.COLLECTIONS.COMPANY_DASHBOARD,
          token: auth.token,
          data: { company: auth.company?.company },
          resourceV2: 'AdminCompanyDashboard',
          companyIdForV2: auth.company?.company,
        }, (_, insertedSettings) => {
          setCompanySettings(insertedSettings);
        });
      }
    };

    findCompanySettings();
  }, []);

  const {
    filterFromDate, setFilterFromDate,
    filterToDate, setFilterToDate,
    filterquickDateOption, setFilterQuickDateOption,
    filterText, setFilterText,
    filterTable, setFilterTable,
    filterType, setFilterType,
    filterRestaurant, setFilterRestaurant,
    sort, setSort, changedSort,
    fetchProcessingOrders, processingOrders, isProcessingLoading,
    fetchClosedOrders, closedOrders, closedTotalCount, isClosedLoading,
    currentClosedPage, onChangeClosedPage,
    filterSnapshot,
  } = useOrders();

  const {
    visibleTab, setVisibleTab,
    orders: processingTabOrders,
    totalNew,
    totalNewTabCombined,
    totalInProgress,
    totalDelivering,
    totalReady,
  } = useOrdersDisplay({ processingOrders, refresher: progressOrdersRefresher, companySettings });

  const tablesRequestData = {
    secretKey: auth.token,
    collectionName: Config.DATA.COLLECTIONS.TABLES,
    formatResult: (result) => {
      sortObjectArray(result, 'number_sort', 1, true);

      return result;
    },
    noFindOnLoad: true,
    resourceV2: 'AdminTable',
    companyIdForV2: auth.company?.company,
  };

  const [findTables, tables] = useFind(tablesRequestData);

  useEffect(() => {
    getCompanyRestaurants({ token: auth.token, companyId: auth.company?.company });
  }, []);

  useEffect(() => {
    if (filterRestaurant) {
      findTables({ ...tablesRequestData, filter: `{ restaurant: ObjectId('${filterRestaurant}') }` }, true);
    }
  }, [filterRestaurant]);

  const onFilterOrders = () => {
    fetchClosedOrders();
    fetchProcessingOrders();
  };

  const onClearFilters = () => {
    setFilterFromDate(getFirstDayOfMonth());
    setFilterToDate(getLastDayOfMonth());
    setFilterText('');
    setFilterTable('');
    setFilterType('');
    // setFilterEmployee('');
    setFilterRestaurant('');
    setFilterQuickDateOption(7);
    onFilterOrders();
  };

  const onSortOrders = (field, dbField, order) => {
    setSort({ field, dbField, order });
    changedSort.value = { field, dbField, order };
    onFilterOrders();
  };

  const onEditClicked = (item) => {
    navigate(`/orders/${item._id}`);
  };

  // This probably needs updating
  /*
  const exportOrders = async () => {
    setIsExporting(true);

    const response = await exportListData({
      token: auth.token,
      alertController,
      filter: requestData.filterBuilder(),
      resource: 'orders',
    });

    if (response && response.fileUrl) {
      window.open(response.fileUrl, '_blank');
    }

    setIsExporting(false);
  }; */

  if (isClosedLoading !== Config.DATA.LOADING_STATUS.INITIAL_LOAD &&
      isProcessingLoading !== Config.DATA.LOADING_STATUS.INITIAL_LOAD &&
      shouldDisplayNoRestaurantBlock()) {
    return <NoRestaurantsBlock />;
  }

  // const { paginatedResults: orders, totalCount } = extractRecordsFromAggreagte(ordersResponse);

  const onProgressStatusChange = async (order, val) => {
    setIsLoadingProgress((prev) => ({ ...prev, [order._id]: true }));

    const response = await updateOrderFields({
      token: auth.token,
      orderId: order._id,
      fields: ['progress_status'],
      values: [val],
      alertController,
      companyId: auth.company?.company,
    });

    setIsLoadingProgress((prev) => ({ ...prev, [order._id]: false }));

    if (!response) {
      return;
    }

    if (response.ordersUpdateResponse && response.ordersUpdateResponse.matchedCount > 0) {
      if ((!order.progress_status || order.progress_status === 'New') && val === 'In_Progress') {
        // eslint-disable-next-line no-param-reassign
        order.keep_in_new = true;
      }

      // eslint-disable-next-line no-param-reassign
      order.progress_status = val;

      if (!order.external_provider) {
        if (val === 'Completed') {
          // eslint-disable-next-line no-param-reassign
          order.status = 'Completed';
          fetchClosedOrders();
        } else if (val === 'Canceled') {
          // eslint-disable-next-line no-param-reassign
          order.status = 'Canceled';
          fetchClosedOrders();
        } else {
          // eslint-disable-next-line no-param-reassign
          order.status = 'New';
        }
      }

      alertController.success({ text: 'Order progress updated' });
    }

    setProgressOrdersRefresher((prev) => prev + 1);
  };

  return (
    <ListLayout isLoading={isClosedLoading === Config.DATA.LOADING_STATUS.INITIAL_LOAD || isProcessingLoading === Config.DATA.LOADING_STATUS.INITIAL_LOAD || companyState.isLoadingRestaurants}>
      <PageTitle title="Orders" isTopLevel>
        {/* <div className="mt-4 flex-shrink-0 flex md:mt-0 md:ml-4">
          <button
            type="button"
            className="ml-3 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            onClick={exportOrders}
            disabled={isExporting}
          >
            {isExporting && (
            <svg className="inline mr-2 w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#e0e0e0" />
              <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="white" />
            </svg>
            )}
            Export CSV
          </button>
            </div> */}
        <div className="mt-4 flex-shrink-0 flex md:mt-0 md:ml-4">
          <FormButton
            disabled={updatingSettings}
            isLoading={updatingSettings}
            onClick={() => {
              setIsSettingsDialogOpen(true);
            }}
            text="Settings"
          />
        </div>
      </PageTitle>

      <PageContent>
        <OrdersFilter
          onClearFilters={onClearFilters}
          isClosedLoading={isClosedLoading}
          isProcessingLoading={isProcessingLoading}
          onFilterOrders={onFilterOrders}
          filterFromDate={filterFromDate}
          setFilterFromDate={setFilterFromDate}
          filterToDate={filterToDate}
          setFilterToDate={setFilterToDate}
          filterRestaurant={filterRestaurant}
          setFilterRestaurant={setFilterRestaurant}
          filterTable={filterTable}
          setFilterTable={setFilterTable}
          filterType={filterType}
          setFilterType={setFilterType}
          filterText={filterText}
          setFilterText={setFilterText}
          restaurants={companyState.restaurants}
          tables={tables}
          filterSnapshot={filterSnapshot}
          filterquickDateOption={filterquickDateOption}
          setFilterQuickDateOption={setFilterQuickDateOption}
        />

        <OrderProgressPills
          currentTab={visibleTab}
          onChange={setVisibleTab}
          totalNew={totalNewTabCombined || totalNew}
          totalInProgress={totalInProgress}
          totalDelivering={totalDelivering}
          totalReady={totalReady}
          companySettings={companySettings}
        />

        {(visibleTab === 'new' || visibleTab === 'in_progress' || visibleTab === 'delivering' || visibleTab === 'ready') && (
        <ProcessingOrdersList
          currentTab={visibleTab}
          orders={processingTabOrders}
          onEditClicked={onEditClicked}
          onProgressStatusChange={onProgressStatusChange}
          isLoadingProgress={isLoadingProgress}
          companySettings={companySettings}
        />
        )}

        {visibleTab === 'closed' && (
        <ClosedOrdersList
          isLoadingProgress={isLoadingProgress}
          onEditClicked={onEditClicked}
          onProgressStatusChange={onProgressStatusChange}
          onSortOrders={onSortOrders}
          orders={closedOrders}
          sort={sort}
          totalCount={closedTotalCount}
          onChangePage={onChangeClosedPage}
          currentPage={currentClosedPage}
        />
        )}
        <OrderSettingsEditForm
          closeModal={() => { setIsSettingsDialogOpen(false); }}
          isOpen={isSettingsDialogOpen}
          itemData={companySettings}
          onSave={async (updateQuery) => {
            setUpdatingSettings(true);
            setIsSettingsDialogOpen(false);
            editById({
              collectionName: Config.DATA.COLLECTIONS.COMPANY_DASHBOARD,
              token: auth.token,
              id: companySettings._id,
              updateClause: { $set: updateQuery },
              resourceV2: 'AdminCompanyDashboard',
              companyIdForV2: auth.company?.company,
              updateTypeV2: 'General',
              updateDataV2: updateQuery,
            }, () => {
              setUpdatingSettings(false);
              setCompanySettings({ ...companySettings, ...updateQuery });
            }, () => {
              setUpdatingSettings(false);
            });
          }}
        />
      </PageContent>
    </ListLayout>
  );
};
